import { Anchor, Breadcrumbs, Card, createStyles, Text } from "@mantine/core";
import { AppShell, MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { Header } from "components/Header";
import NavbarMenu from "components/NavbarMenu";
import { theme } from "mantine-theme";
import { observer } from "mobx-react-lite";
import { PropsWithChildren, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { RootStoreContext } from "stores/RootStore";

const useStyles = createStyles(() => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  breadcrumbs: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  separator: {
    color: "white",
    opacity: 0.6,
  },
  card: {
    marginTop: 20,
    borderRadius: 15,
    paddingTop: 50,
    paddingBottom: 20,
    height: "100%",
    background: "rgba(255, 255, 255, 0.75)",
  },
  headerComponent: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
}));

type Tooltip = {
  title: string;
  href?: string;
};

interface LayoutProps {
  title: string;
  tooltips: Tooltip[];
  plain?: boolean;
  headerContent?: React.ReactNode;
  contentLayout?: "card" | "plain";
}

const Layout: React.FC<PropsWithChildren<LayoutProps>> = observer(
  ({
    children,
    title,
    tooltips,
    plain,
    headerContent,
    contentLayout = "card",
  }) => {
    const { classes } = useStyles();

    const navigate = useNavigate();

    const { authStore, isHydrated } = useContext(RootStoreContext);

    const items = tooltips.map((item, index) => (
      <Anchor
        color="white"
        key={index}
        onClick={() => {
          if (item?.href) {
            navigate(item.href);
          }
        }}
      >
        {item.title}
      </Anchor>
    ));

    return (
      <MantineProvider theme={theme} withGlobalStyles withNormalizeCSS>
        <Notifications position="top-right" />
        <AppShell
          styles={() => ({
            root: {
              background: plain ? "white" : undefined,
            },
            main: {
              background:
                plain || contentLayout === "plain"
                  ? "white"
                  : "var(--background-primary)",
              paddingTop: "35px",
              marginLeft: authStore.isAuthorized && isHydrated ? "64px" : 0,
              maxHeight:
                authStore.isAuthorized && isHydrated
                  ? "calc(100vh - 60px)"
                  : "100vh",
              minHeight:
                authStore.isAuthorized && isHydrated
                  ? "calc(100vh - 60px)"
                  : "100vh",
              overflowY: "scroll",
            },
            header: {
              marginBottom: 0,
            },
          })}
          navbar={
            authStore.isAuthorized && isHydrated ? <NavbarMenu /> : undefined
          }
          header={authStore.isAuthorized && isHydrated ? <Header /> : undefined}
        >
          {plain ? (
            children
          ) : (
            <>
              <div>
                <div className={classes.header}>
                  <Text size={32} weight={600} color="white">
                    {title}
                  </Text>
                  <div className={classes.headerComponent}>
                    {headerContent}
                    {items.length > 0 && (
                      <div className={classes.breadcrumbs}>
                        <Text color="white" opacity={0.6}>
                          You're here:{" "}
                        </Text>
                        <Breadcrumbs
                          classNames={{
                            separator: classes.separator,
                          }}
                        >
                          {items}
                        </Breadcrumbs>
                      </div>
                    )}
                  </div>
                </div>
                {contentLayout === "card" ? (
                  <Card padding={35} className={classes.card}>
                    {children}
                  </Card>
                ) : (
                  <div style={{ paddingTop: 20 }}>{children}</div>
                )}
              </div>
            </>
          )}
        </AppShell>
      </MantineProvider>
    );
  }
);

export { Layout };
